import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, FormControlLabel, IconButton, InputAdornment, MenuItem, Pagination, Skeleton, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { MdAdd, MdClose, MdDeleteOutline, MdEdit, MdRefresh, MdSearch, MdVisibility } from 'react-icons/md';
import { deleteData, getData, postData, putData } from '../../Webservice/Webservice';
import CATEGORY_ROUTES from '../../API_Route/CategoryRoute';

const FONT = 'Mulish, sans-serif';
const LIMIT = 15;

const authToken = () => {
  const raw = sessionStorage.getItem('token');
  return raw ? `Bearer ${raw}` : '';
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const yellowBtn = { textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { background: 'rgb(255 190 0)' } };
const head = { fontFamily: FONT, fontWeight: 800, fontSize: 12.5, color: '#475569', background: '#f8fafc', whiteSpace: 'nowrap' };
const cell = { fontFamily: FONT, fontSize: 13.5 };
const iconBtn = (c) => ({ color: c, background: `${c}14`, borderRadius: '8px', '&:hover': { background: `${c}26` } });
const person = (u) => (u && typeof u === 'object' ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email : null);

const Kpi = ({ title, value, from, to, active, onClick }) => (
  <Box onClick={onClick} sx={{ flex: 1, minWidth: 150, p: 2, borderRadius: '16px', color: '#fff', background: `linear-gradient(135deg,${from},${to})`, cursor: onClick ? 'pointer' : 'default', outline: active ? '3px solid #0f172a' : 'none', outlineOffset: 2 }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 12.5, opacity: 0.9, fontWeight: 700 }}>{title}</Typography>
    <Typography sx={{ fontFamily: FONT, fontSize: 26, fontWeight: 800, lineHeight: 1.15 }}>{value}</Typography>
  </Box>
);

const StatusChip = ({ active }) => (
  <Chip size="small" label={active ? 'Active' : 'Inactive'} sx={{ fontFamily: FONT, fontWeight: 700, height: 22, background: active ? '#dcfce7' : '#f1f5f9', color: active ? '#166534' : '#475569' }} />
);

const Row = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: '1px dashed #e2e8f0' }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{label}</Typography>
    <Typography component="div" sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, textAlign: 'right', wordBreak: 'break-word' }}>{value || value === 0 ? value : '—'}</Typography>
  </Box>
);

const emptyForm = { id: null, name: '', position: '', status: 'active', originalName: '', categoryCount: 0 };

function CategoryCluster() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, empty: 0, categories: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState('');
  const [usage, setUsage] = useState('');
  const seq = useRef(0);

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [viewing, setViewing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: LIMIT, search: debounced, status, usage };
      Object.keys(params).forEach((k) => params[k] === '' && delete params[k]);
      const res = await getData(CATEGORY_ROUTES.LIST_CATEGORY_CLUSTER, params, authToken(), false);
      if (mine !== seq.current) return;
      setRows(res?.clusters || []);
      setMeta({ total: res?.meta?.total || 0, totalPages: res?.meta?.totalPages || 1 });
      if (res?.stats) setStats(res.stats);
    } catch {
      if (mine === seq.current) setError('Could not load category clusters.');
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [page, debounced, status, usage]);
  useEffect(() => { load(); }, [load]);

  const dirty = !!search || !!status || !!usage;

  const openForm = (c = null) => {
    setViewing(null);
    setErrors({});
    setFormError('');
    setForm(c ? { id: c._id, name: c.name, position: c.position ? String(c.position) : '', status: c.status || 'active', originalName: c.name, categoryCount: c.categoryCount || 0 } : emptyForm);
    setFormOpen(true);
  };

  const save = async (ev) => {
    ev.preventDefault();
    setFormError('');
    const e = {};
    if (!form.name.trim()) e.name = 'Cluster name is required';
    else if (form.name.trim().length > 60) e.name = 'Keep it under 60 characters';
    if (form.position !== '' && !/^\d{1,6}$/.test(String(form.position).trim())) e.position = 'Whole number, 1 or more (leave blank for none)';
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), status: form.status, position: form.position === '' ? 0 : Number(form.position) };
      if (form.id) await putData(`category-cluster/update/${form.id}`, payload, authToken(), true);
      else await postData('category-cluster/add', payload, authToken(), true);
      setFormOpen(false);
      setNotice(form.id ? 'Cluster updated.' : 'Cluster created.');
      load();
    } catch (err) {
      setFormError(err?.message || 'Could not save the cluster.');
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (c) => {
    try {
      await putData(`category-cluster/update/${c._id}`, { status: c.status === 'active' ? 'inactive' : 'active' }, authToken(), true);
      load();
    } catch { /* the service shows the error */ }
  };

  const openView = async (c) => {
    setViewing(c);
    try {
      const res = await getData(`category-cluster/${c._id}`, undefined, authToken(), false);
      if (res?._id) setViewing((cur) => (cur && cur._id === c._id ? { ...cur, ...res } : cur));
    } catch { /* keep the row data */ }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteData(`category-cluster/delete/${toDelete._id}`, authToken());
      setToDelete(null);
      setNotice('Cluster deleted.');
      if (rows.length === 1 && page > 1) setPage(page - 1); else load();
    } catch (err) {
      setDeleteError(err?.message || 'Could not delete the cluster.');
    } finally {
      setDeleting(false);
    }
  };

  const renaming = form.id && form.name.trim() && form.name.trim().toLowerCase() !== form.originalName.toLowerCase();

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Category clusters</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>Groups of categories. A cluster’s position sets its order in the navigation.</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh"><span><IconButton onClick={load} disabled={loading} sx={{ background: '#f1f5f9', borderRadius: '10px' }}><MdRefresh size={20} /></IconButton></span></Tooltip>
          <Button startIcon={<MdAdd />} variant="contained" onClick={() => openForm()} sx={yellowBtn}>Add cluster</Button>
        </Box>
      </Box>

      {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 2 }}>{notice}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <Kpi title="All clusters" value={stats.total} from="#6366f1" to="#4338ca" active={!status && !usage} onClick={() => { setStatus(''); setUsage(''); setPage(1); }} />
        <Kpi title="Active" value={stats.active} from="#22c55e" to="#15803d" active={status === 'active'} onClick={() => { setStatus(status === 'active' ? '' : 'active'); setPage(1); }} />
        <Kpi title="Inactive" value={stats.inactive} from="#94a3b8" to="#475569" active={status === 'inactive'} onClick={() => { setStatus(status === 'inactive' ? '' : 'inactive'); setPage(1); }} />
        <Kpi title="Empty clusters" value={stats.empty} from="#f59e0b" to="#b45309" active={usage === 'empty'} onClick={() => { setUsage(usage === 'empty' ? '' : 'empty'); setPage(1); }} />
        <Kpi title="Categories grouped" value={stats.categories} from="#0ea5e9" to="#0369a1" />
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, p: 1.75, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px' }}>
        <TextField size="small" placeholder="Search cluster name…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 240 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch size={18} /></InputAdornment> }} />
        <TextField select size="small" label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} sx={{ minWidth: 150 }}>
          <MenuItem value="">All</MenuItem><MenuItem value="active">Active</MenuItem><MenuItem value="inactive">Inactive</MenuItem>
        </TextField>
        <TextField select size="small" label="Categories" value={usage} onChange={(e) => { setUsage(e.target.value); setPage(1); }} sx={{ minWidth: 170 }}>
          <MenuItem value="">Any</MenuItem><MenuItem value="used">Has categories</MenuItem><MenuItem value="empty">Empty</MenuItem>
        </TextField>
        {dirty && <Button onClick={() => { setSearch(''); setStatus(''); setUsage(''); setPage(1); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Clear</Button>}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={head} align="center">Position</TableCell>
                <TableCell sx={head}>Cluster</TableCell>
                <TableCell sx={head} align="center">Categories</TableCell>
                <TableCell sx={head}>Status</TableCell>
                <TableCell sx={head}>Added</TableCell>
                <TableCell sx={head} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && [...Array(6)].map((_, i) => <TableRow key={i}>{[...Array(6)].map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)}
              {!loading && rows.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6, fontFamily: FONT, color: '#64748b' }}>{dirty ? 'No clusters match these filters.' : 'No clusters yet. Add the first one.'}</TableCell></TableRow>}
              {!loading && rows.map((c) => (
                <TableRow key={c._id} hover>
                  <TableCell align="center"><Chip size="small" label={c.position || '—'} sx={{ fontWeight: 800, height: 24, minWidth: 34, background: c.position ? '#e0e7ff' : '#f1f5f9', color: c.position ? '#3730a3' : '#94a3b8' }} /></TableCell>
                  <TableCell sx={cell}><Typography onClick={() => openView(c)} sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5, cursor: 'pointer', '&:hover': { color: '#4338ca' } }}>{c.name}</Typography></TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={c.categoryCount || 0} sx={{ fontWeight: 800, height: 22, background: c.categoryCount ? '#dcfce7' : '#fef3c7', color: c.categoryCount ? '#166534' : '#92400e' }} />
                    {c.categoryCount > 0 && c.activeCategoryCount < c.categoryCount && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#94a3b8' }}>{c.activeCategoryCount} active</Typography>}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Switch size="small" checked={c.status === 'active'} onChange={() => toggle(c)} />
                      <StatusChip active={c.status === 'active'} />
                    </Box>
                  </TableCell>
                  <TableCell sx={cell}>{fmtDate(c.createdAt)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="View"><IconButton size="small" onClick={() => openView(c)} sx={iconBtn('#4f46e5')}><MdVisibility size={17} /></IconButton></Tooltip>{' '}
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => openForm(c)} sx={iconBtn('#ca8a04')}><MdEdit size={17} /></IconButton></Tooltip>{' '}
                    <Tooltip title={c.categoryCount ? 'In use by categories' : 'Delete'}><IconButton size="small" onClick={() => { setDeleteError(''); setToDelete(c); }} sx={iconBtn('#dc2626')}><MdDeleteOutline size={17} /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{meta.total} cluster{meta.total === 1 ? '' : 's'}</Typography>
          <Pagination count={meta.totalPages} page={page} onChange={(_, p) => setPage(p)} shape="rounded" size="small" />
        </Box>
      </Box>

      {/* create / edit */}
      <Dialog open={formOpen} onClose={() => !saving && setFormOpen(false)} maxWidth="xs" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: '18px', background: '#f8fafc' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2.25, borderBottom: '3px solid rgb(255 209 2)', background: '#fff' }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 20 }}>{form.id ? 'Edit cluster' : 'Add cluster'}</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{form.id ? form.originalName : 'A group of related categories'}</Typography>
          </Box>
          <IconButton onClick={() => setFormOpen(false)} disabled={saving} size="small"><MdClose size={20} /></IconButton>
        </Box>
        <Box component="form" onSubmit={save} noValidate sx={{ display: 'contents' }}>
          <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField autoFocus size="small" label="Cluster name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} error={!!errors.name} helperText={errors.name} inputProps={{ maxLength: 60 }} />
            <TextField size="small" type="number" label="Position" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} error={!!errors.position}
              helperText={errors.position || 'Order in the navigation: 1 shows first. Each position can be used once. Leave blank for none.'} inputProps={{ min: 0 }} />
            <FormControlLabel control={<Switch checked={form.status === 'active'} onChange={(e) => setForm({ ...form, status: e.target.checked ? 'active' : 'inactive' })} />} label={form.status === 'active' ? 'Active' : 'Inactive'} />
            {renaming && form.categoryCount > 0 && <Alert severity="info">{form.categoryCount} categor{form.categoryCount === 1 ? 'y stays' : 'ies stay'} in this cluster under the new name.</Alert>}
            {formError && <Alert severity="error">{formError}</Alert>}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
            <Button onClick={() => setFormOpen(false)} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving} sx={yellowBtn}>{saving ? 'Saving…' : form.id ? 'Save changes' : 'Add cluster'}</Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* view */}
      <Dialog open={!!viewing} onClose={() => setViewing(null)} maxWidth="sm" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: '18px', background: '#f8fafc' } }}>
        {viewing && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2.25, background: 'linear-gradient(135deg,#6366f1,#4338ca)', color: '#fff' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 20 }}>{viewing.name}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, opacity: 0.9 }}>{viewing.position ? `Position ${viewing.position}` : 'No fixed position'} · {viewing.categoryCount || 0} categor{viewing.categoryCount === 1 ? 'y' : 'ies'}</Typography>
              </Box>
              <StatusChip active={viewing.status === 'active'} />
              <IconButton onClick={() => setViewing(null)} size="small" sx={{ color: '#fff' }}><MdClose size={20} /></IconButton>
            </Box>
            <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: '4px solid #f59e0b', borderRadius: '14px', p: 2.25 }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 0.5 }}>Details</Typography>
                <Row label="Position" value={viewing.position || 'None'} />
                <Row label="Added" value={fmtDate(viewing.createdAt)} />
                <Row label="Added by" value={person(viewing.createdBy)} />
                <Row label="Last updated" value={fmtDate(viewing.updatedAt)} />
                <Row label="Updated by" value={person(viewing.updatedBy)} />
              </Box>
              <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: '4px solid #22c55e', borderRadius: '14px', p: 2.25 }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 1 }}>Categories in this cluster</Typography>
                {!viewing.categories && <Skeleton height={48} />}
                {viewing.categories && viewing.categories.length === 0 && <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>No categories use this cluster yet.</Typography>}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {(viewing.categories || []).map((cat) => (
                    <Chip key={cat._id} label={cat.title} size="small" sx={{ fontFamily: FONT, fontWeight: 700, background: cat.status === 'active' ? '#e0e7ff' : '#f1f5f9', color: cat.status === 'active' ? '#3730a3' : '#94a3b8', textDecoration: cat.status === 'active' ? 'none' : 'line-through' }} />
                  ))}
                </Box>
                {viewing.categories?.some((cat) => cat.status !== 'active') && <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#94a3b8', mt: 1 }}>Struck-through categories are inactive.</Typography>}
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
              <Button onClick={() => setViewing(null)} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Close</Button>
              <Button onClick={() => openForm(viewing)} startIcon={<MdEdit />} variant="contained" sx={yellowBtn}>Edit cluster</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* delete */}
      <Dialog open={!!toDelete} onClose={() => !deleting && setToDelete(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18, mb: 1 }}>Delete cluster?</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#475569' }}>
            <b>{toDelete?.name}</b> will be removed (a copy is archived).{toDelete?.categoryCount ? ` ${toDelete.categoryCount} categor${toDelete.categoryCount === 1 ? 'y uses' : 'ies use'} it, so the server will refuse until ${toDelete.categoryCount === 1 ? 'it is' : 'they are'} moved. You can switch it off instead.` : ' No categories use it.'}
          </Typography>
          {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setToDelete(null)} disabled={deleting} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={confirmDelete} disabled={deleting} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, background: '#dc2626', '&:hover': { background: '#b91c1c' } }}>{deleting ? 'Deleting…' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CategoryCluster;
